import React, { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { useLocation, useNavigationType, createRoutesFromChildren, matchRoutes } from 'react-router-dom';
import { useCookieConsent } from '../contexts/CookieConsentContext';

export const SentryInit: React.FC = () => {
  const { hasConsent } = useCookieConsent();
  const location = useLocation();
  const navigationType = useNavigationType();
  const analyticsConsent = hasConsent('analytics');

  useEffect(() => {
    if (!import.meta.env.VITE_SENTRY_DSN) {
      return;
    }

    if (window.__SENTRY_INITIALIZED__) {
      return;
    }

    const integrations = [];

    if (analyticsConsent) {
      integrations.push(
        Sentry.reactRouterV7BrowserTracingIntegration({
          useEffect: React.useEffect,
          useLocation: () => location,
          useNavigationType: () => navigationType,
          createRoutesFromChildren,
          matchRoutes,
        }),
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
        }),
      );
    }

    console.log('Initializing Sentry with DSN:', import.meta.env.VITE_SENTRY_DSN);
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      environment: import.meta.env.MODE || 'development',
      integrations,
      tracesSampleRate: analyticsConsent
        ? (import.meta.env.MODE === 'production' ? 0.1 : 1.0)
        : 0,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: analyticsConsent ? 1.0 : 0,
      beforeSend(event) {
        if (typeof window !== 'undefined') {
          event.contexts = {
            ...event.contexts,
            request: {
              url: window.location.href,
              headers: {
                'User-Agent': navigator.userAgent,
              },
              method: 'GET',
            },
          };
        }
        return event;
      },
    });

    window.__SENTRY_INITIALIZED__ = true;
  }, [analyticsConsent, location, navigationType]);

  return null;
};

declare global {
  interface Window {
    __SENTRY_INITIALIZED__?: boolean;
  }
}
